/**
 * The two cards the ONLINE flow still mounts outside the arena menu, plus the
 * types `main.ts` and `arena-menu.ts` share.
 *
 * ── WHAT THIS FILE IS, AND WHAT IT WAS ────────────────────────────────────
 *
 * Until 2026-08-17 this was the ORIGINAL 1,480-line title flow —
 * `mountTitleFlow`, its mode cards, parallax, tactics/setup segments, and a
 * whole second online-lobby stack under `ui/online/*` (OnlineMatchService, a
 * LocalMockOnlineTransport that kept state in one tab, and a
 * ServerOnlineTransport that rejected every call with "Online play is not
 * available yet"). None of it had a caller: `ui/arena-menu.ts` replaced the
 * front end on 2026-08-09 and `mountTitleFlow` was never invoked again.
 *
 * It was deleted (CR-11) because dead code that LOOKS like the live path is
 * worse than none — the parallel online stack was the first thing an agent
 * chasing "the other player can't pick a position" opened, and it was the
 * wrong screen entirely. What survives here is exactly what still runs:
 *
 *   - `mountNamePrompt` — the §9.4 guest-entry card a `?join=CODE` deep link
 *     lands on ("JOINING MATCH XXXXXX · PLAYER NAME · PLAY"). Sacred: an
 *     invite link never sees the menu.
 *   - `mountVsCard`     — the pre-match "A vs B · PREPARING PITCH…" card.
 *   - `TitleFlowHandlers` / `TitleFlow` / `MatchSetup` / `StartScreen` /
 *     `RoomSquadSize` — the contract between `main.ts` and `arena-menu.ts`.
 *
 * `CSS` below is kept whole rather than trimmed to the join card's selectors:
 * the card genuinely uses ~7 of them and cutting the rest saves nothing worth
 * a visual regression on the one screen every invite link goes through.
 */

import type { MatchRoster, SquadSize } from '@fg/shared/sim';
import type { Difficulty } from '@fg/shared/ai';
import { deriveKit } from '../render/kit-colors.js';
import type { TimeOfDay, Weather } from './settings.js';
import { ACCENT, FIFA_CSS, el } from './fifa-ui.js';
import type { CompetitionContext } from './tournament-store.js';

/** Screen fade-out, ms — `.fgmenu-exit`'s `fgmenuOut` animation in `CSS`
 *  below. Only the join card mounts that stylesheet now, but the rule is
 *  part of it and the number stays with the rule. */
const SWAP_MS = 240;

export interface MatchSetup {
  squad: SquadSize;
  difficulty: Difficulty;
  halfMinutes: number;
  timeOfDay: TimeOfDay;
  weather: Weather;
  competition?: CompetitionContext;
  roster?: MatchRoster;
}

/**
 * Squad sizes a host may open a private room at. Mirrors the lobby's own
 * MATCH_MODES table (@fg/lobby/types) — every one of these has a matching
 * entry in @fg/shared's DEFAULT_ROSTERS, which is what squad-remap.ts needs to
 * translate a picked position onto the deployed 11-a-side server's real slot.
 * Adding a size here without one there would throw at seat time, by design.
 */
export type RoomSquadSize = '1v1' | '2v2' | '3v3' | '4v4' | '5v5' | '7v7' | '11v11';

export interface TitleFlowHandlers {
  onStartPractice(setup: MatchSetup): void;
  onQuickMatch(name: string): void;
  /** `mode` is the squad size the HOST picked on the Create Room screen; it
   *  sets how many of the real XI's slots are open to humans (AI fills the
   *  rest, exactly as ranked's 5v5 does — see squad-remap.ts). */
  onCreateFriend(name: string, mode: RoomSquadSize): void;
  onJoinCode(name: string, code: string): void;
  /** Rated matchmaking (docs/ACCOUNTS-SPEC.md phase 3) — 11v11 is the
   *  flagship ranked mode; 5v5 plays for real via squad-remap.ts's shim. */
  onRankedMatch(name: string, mode: '11v11' | '5v5'): void;
  onOpenSettings(): void;
  onStartArena(): void;
  onStartSolo(): void;
  persist(): void;
}

export interface TitleFlow {
  setStatus(text: string): void;
  setBusy(busy: boolean): void;
  dispose(): void;
}

export type StartScreen =
  | 'title' | 'modes' | 'career' | 'worldcup' | 'teams'
  | 'online' | 'bots' | 'practice' | 'profile';


function cleanName(raw: string): string {
  return raw.trim().slice(0, 16);
}

function linearToSrgbHex(rgb: [number, number, number]): string {
  const conv = (c: number): number => {
    const s = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.max(0, Math.min(255, Math.round(s * 255)));
  };
  return `#${((1 << 24) | (conv(rgb[0]!) << 16) | (conv(rgb[1]!) << 8) | conv(rgb[2]!)).toString(16).slice(1)}`;
}
const TEAM_A_CSS = linearToSrgbHex(deriveKit(0).primary);
const TEAM_B_CSS = linearToSrgbHex(deriveKit(1).primary);

export interface VsCard {
  dispose(): void;
}

export function mountVsCard(
  container: HTMLElement,
  nameA: string,
  nameB: string,
  competitionLabel = '',
): VsCard {
  const root = el('div', 'fgvs');
  const safe = (s: string): string => s.replace(/[<>&]/g, '');
  root.innerHTML = `
    ${competitionLabel ? `<p class="fgvs-comp">${safe(competitionLabel)}</p>` : ''}
    <div class="fgvs-inner">
      <div class="fgvs-side" style="--kit:${TEAM_A_CSS}">
        <span class="fgvs-chip"></span><span class="fgvs-name">${safe(nameA)}</span>
      </div>
      <span class="fgvs-vs">VS</span>
      <div class="fgvs-side fgvs-right" style="--kit:${TEAM_B_CSS}">
        <span class="fgvs-name">${safe(nameB)}</span><span class="fgvs-chip"></span>
      </div>
    </div>
    <p class="fgvs-loading">PREPARING PITCH…</p>`;
  container.appendChild(root);
  const style = document.createElement('style');
  style.textContent = VS_CSS;
  document.head.appendChild(style);
  return {
    dispose(): void {
      root.classList.add('fgvs-out');
      window.setTimeout(() => {
        root.remove();
        style.remove();
      }, 380);
    },
  };
}

export function mountNamePrompt(
  container: HTMLElement,
  presetName: string,
  code: string,
): Promise<string> {
  return new Promise((resolve) => {
    const root = el('div', 'fgmenu');
    root.appendChild(el('div', 'fgmenu-bg'));
    const s = el('div', 'fgmenu-stage');
    s.innerHTML = `
      <div class="fgmenu-screen">
        <div class="fgmenu-mark" style="--i:0">⚽</div>
        <h1 class="fgmenu-word fgmenu-word-small" style="--i:1">FOOTY <span>ARENA</span></h1>
        <p class="fgmenu-sub" style="--i:2">JOINING MATCH <b>${code.replace(/[^A-Za-z0-9]/g, '').toUpperCase()}</b></p>
        <form class="fgmenu-stack" style="--i:3">
          <label class="fgmenu-name">
            <span>PLAYER NAME</span>
            <input data-fg="name" maxlength="16" placeholder="Guest" autocomplete="off">
          </label>
          <button type="submit" class="fgmenu-btn fgmenu-primary">PLAY</button>
          <p class="fgmenu-foot">No account needed.</p>
        </form>
      </div>`;
    root.appendChild(s);
    container.appendChild(root);

    const style = document.createElement('style');
    style.textContent = CSS + FIFA_CSS;
    document.head.appendChild(style);

    const input = root.querySelector('[data-fg="name"]') as HTMLInputElement;
    input.value = presetName;
    (root.querySelector('form') as HTMLFormElement).addEventListener('submit', (e) => {
      e.preventDefault();
      const name = cleanName(input.value) || 'Guest';
      root.remove();
      style.remove();
      resolve(name);
    });
    input.focus();
  });
}

const CSS = `
.fgmenu {
  position: fixed; inset: 0; z-index: 20;
  display: grid; place-items: center;
  background: #0a0e15;
  font-family: 'Segoe UI', 'Helvetica Neue', system-ui, sans-serif;
  color: #fff; user-select: none; overflow: hidden;
}
.fgmenu-busy { cursor: progress; }
.fgmenu-busy .fgmenu-btn, .fgmenu-busy .fgmenu-card, .fgmenu-busy .fgfe-tile { opacity: 0.55; pointer-events: none; }

/* ── Shared backdrop ──────────────────────────────────────────────────────
   Every screen that is not the title or the dashboard used to sit on flat
   #0a0e15 with two drifting gradient blobs behind it, so leaving the dashboard
   meant leaving the stadium and landing in a settings app. This puts the same
   floodlit plate behind ALL of them — blurred to 26px and pulled down to a
   third brightness, so it is scenery rather than content and never competes
   with the panel in front of it.

   The plate goes on ::before and the scrim on ::after, deliberately: \`filter\`
   applies to an element's pseudo-elements too, so a single element carrying
   both would blur its own scrim.

   The title screen and the dashboard paint their own opaque plates over the
   top of this, so it costs nothing there. */
.fgmenu-bg { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.fgmenu-bg::before, .fgmenu-bg::after { content: ''; position: absolute; }
.fgmenu-bg::before {
  inset: -12%;
  background: url('/branding/menu-hero.jpg') center 45% / cover no-repeat;
  filter: blur(24px) saturate(0.8) brightness(0.5) contrast(1.04);
  animation: fgmenuDrift 44s ease-in-out infinite alternate;
}
.fgmenu-bg::after {
  inset: 0;
  background:
    radial-gradient(62% 50% at 68% 18%, rgba(255,226,168,0.08) 0%, rgba(255,226,168,0) 70%),
    linear-gradient(180deg, rgba(4,7,12,0.6) 0%, rgba(4,7,12,0.24) 36%, rgba(4,7,12,0.5) 78%, rgba(4,7,12,0.82) 100%);
}
/* Slow enough to be scenery. The old 26s/6% drift was fast enough to catch the
   eye while you were reading a panel in front of it. */
@keyframes fgmenuDrift {
  from { transform: translate3d(-1.2%, -0.8%, 0) scale(1.02); }
  to   { transform: translate3d(1.2%, 0.8%, 0) scale(1.07); }
}

.fgmenu-stage { position: relative; z-index: 1; width: 100vw; height: 100vh; }
.fgmenu-screen { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.fgmenu-screen > * {
  animation: fgmenuIn 260ms cubic-bezier(0.2, 0.9, 0.3, 1) both;
  animation-delay: calc(var(--i, 0) * 55ms);
}
.fgmenu-exit { pointer-events: none; }
/* Exits ACCELERATE away (ease-in) and recede — the opposite curve and the
   opposite direction to every arrival. The old exit scaled UP while fading,
   which put the outgoing screen and the incoming one on the same trajectory
   and made every route change read as one crossfade. */
.fgmenu-exit > * {
  animation: fgmenuOut ${SWAP_MS}ms cubic-bezier(0.55, 0, 0.85, 0.35) forwards !important;
}
@keyframes fgmenuIn {
  from { opacity: 0; transform: translate3d(0, 12px, 0) scale(0.992); }
  to   { opacity: 1; transform: none; }
}
@keyframes fgmenuOut {
  to { opacity: 0; transform: translate3d(0, -14px, 0) scale(0.985); }
}

.fgmenu-titlescreen { padding: 40px 0; justify-content: center; height: 100%; }
.fgmenu-sub { margin: 0; font-size: 11px; letter-spacing: 3px; color: rgba(255,255,255,0.55); }
.fgmenu-sub b { color: ${ACCENT}; }
.fgmenu-anykey {
  margin: 26px 0 0; font-size: 13px; font-weight: 800; letter-spacing: 4px; color: ${ACCENT};
  animation: fgmenuIn 260ms both, fgmenuPulse 1.6s ease-in-out 400ms infinite !important;
}
@keyframes fgmenuPulse { 50% { opacity: 0.35; } }

.fgmenu-btn {
  padding: 12px 16px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.22);
  background: linear-gradient(180deg, #232b3d 0%, #161c29 100%);
  color: #fff; font-weight: 800; letter-spacing: 2px; font-size: 13px; cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.08), 0 2px 10px rgba(0,0,0,0.4);
  transition: transform 120ms ease, border-color 120ms ease, color 120ms ease;
}
.fgmenu-btn:hover, .fgmenu-btn:focus-visible { border-color: ${ACCENT}; color: ${ACCENT}; transform: translateY(-1px); outline: none; }
.fgmenu-primary {
  background: linear-gradient(180deg, #ffe27a 0%, #f5c334 100%);
  color: #171b26; border: 0;
}
.fgmenu-primary:hover, .fgmenu-primary:focus-visible { color: #000; filter: brightness(1.06); }
.fgmenu-ghost { background: none; box-shadow: none; border-style: dashed; }

.fgmenu-setup {
  display: flex; flex-direction: column; gap: 10px; width: 100%;
  padding: 0; border: 0; background: none; box-shadow: none;
}
.fgmenu-segrow { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
.fgmenu-seglabel { font-size: 10px; font-weight: 800; letter-spacing: 2px; color: rgba(255,255,255,0.55); flex: 0 0 92px; }
.fgmenu-seg { display: flex; gap: 4px; flex-wrap: wrap; }
.fgmenu-segbtn {
  padding: 6px 10px; border-radius: 3px; border: 1px solid rgba(255,255,255,0.16);
  background: rgba(13,17,23,0.7); color: rgba(255,255,255,0.75);
  font-size: 11px; font-weight: 800; letter-spacing: 1px; cursor: pointer;
  transition: border-color 120ms ease, background 120ms ease, color 120ms ease;
}
.fgmenu-segbtn:hover, .fgmenu-segbtn:focus-visible { border-color: rgba(255,217,74,0.6); outline: none; }
.fgmenu-segon {
  background: linear-gradient(180deg, #ffe27a 0%, #f5c334 100%);
  color: #171b26; border-color: transparent;
}

.fgmenu-logo { display:block; object-fit:contain; filter:drop-shadow(0 18px 28px rgba(0,0,0,.72)); }
.fgmenu-logo-hero { width:min(430px,84vw); max-height:54vh; }

.fgmenu-status {
  display: none; position: absolute; bottom: 26px; left: 50%; transform: translateX(-50%);
  z-index: 200; margin: 0; text-align: center; font-size: 12px; color: #ff8a8a;
  background: rgba(80,20,20,0.85); border: 1px solid rgba(255,100,100,0.5);
  border-radius: 4px; padding: 7px 14px; max-width: min(480px, calc(100vw - 40px));
}
`;

const VS_CSS = `
.fgvs {
  position: fixed; inset: 0; z-index: 26;
  display: grid; place-items: center; align-content: center; gap: 18px;
  background: radial-gradient(900px 480px at 50% 40%, #141b28, #0a0e15 75%);
  font-family: 'Segoe UI', 'Helvetica Neue', system-ui, sans-serif;
  color: #fff; user-select: none;
  animation: fgvsIn 320ms ease-out;
}
.fgvs-out { animation: fgvsOut 360ms ease-in forwards; }
@keyframes fgvsIn { from { opacity: 0; } }
@keyframes fgvsOut { to { opacity: 0; } }
.fgvs-comp {
  margin: 0; font-size: 11px; font-weight: 900; letter-spacing: 3px; color: ${ACCENT};
  animation: fgvsPop 460ms cubic-bezier(0.2, 1.4, 0.3, 1) both;
}
.fgvs-inner { display: flex; align-items: center; gap: 26px; }
.fgvs-side { display: flex; align-items: center; gap: 12px; animation: fgvsSlide 420ms cubic-bezier(0.2, 0.9, 0.3, 1) both; }
.fgvs-right { animation-name: fgvsSlideR; }
@keyframes fgvsSlide { from { opacity: 0; transform: translateX(-40px); } }
@keyframes fgvsSlideR { from { opacity: 0; transform: translateX(40px); } }
.fgvs-chip { width: 14px; height: 34px; border-radius: 2px; background: var(--kit); transform: skewX(-10deg);
  box-shadow: 0 0 14px rgba(0,0,0,0.5), inset 0 2px 0 rgba(255,255,255,0.25); }
.fgvs-name { font-size: 30px; font-weight: 900; letter-spacing: 3px; font-style: italic; text-align: center; }
.fgvs-vs { font-size: 16px; font-weight: 900; color: ${ACCENT}; letter-spacing: 2px;
  animation: fgvsPop 460ms cubic-bezier(0.2, 1.4, 0.3, 1) 160ms both; }
@keyframes fgvsPop { from { opacity: 0; transform: scale(0.4); } }
.fgvs-loading { margin: 0; font-size: 11px; letter-spacing: 3px; color: rgba(255,255,255,0.5);
  animation: fgvsPulse 1.2s ease-in-out infinite; }
@keyframes fgvsPulse { 50% { opacity: 0.35; } }
`;
